import { useState, useCallback, useRef, useEffect } from 'react'
import { apiFetch } from '@/lib/api'

export interface UseApiFetchResult<T> {
  data: T | null
  error: string | null
  isLoading: boolean
  /** 재조회 포함 요청 진행 중 — SWR 패턴이라 기존 데이터가 있으면 isLoading은 false로 유지된다 */
  isFetching: boolean
  refetch: () => void
  mutate: (data: T) => void
}

interface UseApiFetchOptions {
  revalidateOnFocus?: boolean
}

export function useApiFetch<T = unknown>(path: string | null, options?: UseApiFetchOptions): UseApiFetchResult<T> {
  const [state, setState] = useState<{ data: T | null; error: string | null; isLoading: boolean; isFetching: boolean }>({
    data: null, error: null, isLoading: !!path, isFetching: false,
  })
  const pathRef = useRef(path)

  const fetchData = useCallback(async () => {
    const currentPath = pathRef.current
    if (!currentPath) return
    // 기존 데이터가 없을 때만 isLoading 표시 (초기 로딩)
    // refetch 시에는 기존 데이터를 유지하며 백그라운드 갱신 (SWR 패턴) — 진행 여부는 isFetching으로 노출
    setState(s => s.data
      ? { ...s, isFetching: true, error: null }
      : { ...s, isLoading: true, isFetching: true, error: null }
    )
    try {
      const data = await apiFetch<T>(currentPath)
      setState({ data, error: null, isLoading: false, isFetching: false })
    } catch (e: any) {
      // refetch 실패 시에도 기존 data 유지
      setState(s => ({ data: s.data, error: e.message, isLoading: false, isFetching: false }))
    }
  }, [])

  useEffect(() => {
    pathRef.current = path
    if (path) fetchData()
    else setState({ data: null, error: null, isLoading: false, isFetching: false })
  }, [path, fetchData])

  // 탭 포커스 시 자동 refetch
  useEffect(() => {
    if (!options?.revalidateOnFocus) return
    const onFocus = () => fetchData()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [options?.revalidateOnFocus, fetchData])

  return {
    ...state,
    refetch: fetchData,
    mutate: (data: T) => setState(s => ({ ...s, data })),
  }
}
